import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Fish, History } from "lucide-react";
import StatsPage from "./Stats";
import { formatDateTime, weightLabel } from "../lib/format";

const MONTH = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });

type MonthGroup = { key: string; label: string; sessions: any[] };
type YearGroup = { year: string; months: MonthGroup[] };

export default function StatsWithHistoryPage() {
  const sessionsQuery = useQuery({
    queryKey: ["carpintel_sessions"],
    queryFn: () => fetch("/app-api/sessions").then((r) => (r.ok ? r.json() : [])),
  });
  const catchesQuery = useQuery({
    queryKey: ["carpintel_catches"],
    queryFn: () => fetch("/app-api/catches").then((r) => (r.ok ? r.json() : [])),
  });
  const intelligenceQuery = useQuery({
    queryKey: ["catchcompass_tackle_intelligence"],
    queryFn: () => fetch("/app-api/tackle-intelligence").then((r) => (r.ok ? r.json() : { components: [], feed: [] })),
  });
  const positionQuery = useQuery({
    queryKey: ["catchcompass_position_intelligence"],
    queryFn: () => fetch("/app-api/position-intelligence").then((r) => (r.ok ? r.json() : { positions: [] })),
  });

  const sessions: any[] = sessionsQuery.data ?? [];
  const catches: any[] = catchesQuery.data ?? [];
  const components: any[] = intelligenceQuery.data?.components ?? [];
  const feedEvents: any[] = intelligenceQuery.data?.feed ?? [];
  const positions: any[] = positionQuery.data?.positions ?? [];

  const years: YearGroup[] = useMemo(() => {
    const byYear = new Map<string, Map<string, MonthGroup>>();
    for (const session of sessions) {
      const date = new Date(session.start_at);
      if (!Number.isFinite(date.getTime())) continue;
      const year = String(date.getFullYear());
      const monthKey = `${year}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!byYear.has(year)) byYear.set(year, new Map());
      const yearMap = byYear.get(year)!;
      if (!yearMap.has(monthKey)) yearMap.set(monthKey, { key: monthKey, label: MONTH.format(date), sessions: [] });
      yearMap.get(monthKey)!.sessions.push(session);
    }
    return [...byYear.entries()]
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([year, months]) => ({ year, months: [...months.values()].sort((a, b) => b.key.localeCompare(a.key)) }));
  }, [sessions]);

  const feedSummary = useMemo(() => {
    const groups = new Map<string, { label: string; events: number; catches: number }>();
    for (const event of feedEvents) {
      const mix = Array.isArray(event.mix_json) ? event.mix_json : [];
      const label = mix.map((item: any) => String(item.snapshot ?? "").trim()).filter(Boolean).join(" + ") || "Feed mix";
      const key = label.toLowerCase();
      const row = groups.get(key) ?? { label, events: 0, catches: 0 };
      row.events += 1;
      row.catches += Number(event.catches_within_2h ?? 0);
      groups.set(key, row);
    }
    return [...groups.values()].sort((a, b) => b.events - a.events).slice(0, 8);
  }, [feedEvents]);

  const recentCatches = catches.slice(0, 10);

  return (
    <>
      <StatsPage />
      <div className="mx-auto -mt-20 max-w-[1200px] space-y-8 px-4 pb-28 md:-mt-4 md:pb-10">
        <section className="border-t border-border pt-6">
          <h2 className="text-lg font-semibold text-primary">Tackle evidence</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">Lead system, rig and hookbait now keep their own exposure clocks. Conversion is only one signal, and small samples stay labelled as early evidence.</p>
          {components.length === 0 ? <p className="mt-3 rounded-lg border border-dashed border-border p-4 text-sm text-secondary">Independent tackle evidence will build from sessions using the new live rod controls.</p> : (
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              {(["lead_system", "rig", "hookbait"] as const).map((type) => (
                <div key={type} className="rounded-xl border border-border bg-raised p-4">
                  <h3 className="font-semibold capitalize text-primary">{type.replace("_", " ")}</h3>
                  <div className="mt-3 space-y-3">
                    {components.filter((row) => row.component_type === type).slice(0, 8).map((row) => (
                      <div key={`${type}-${row.label}`} className="rounded-lg bg-background p-3">
                        <p className="truncate text-sm font-semibold text-primary">{row.label}</p>
                        <p className="mt-1 text-xs text-secondary">{row.take_events} take{Number(row.take_events) === 1 ? "" : "s"} · {row.conversion_pct == null ? "No conversion yet" : `${row.conversion_pct}% converted`} · {row.exposure_hours == null ? "Exposure building" : `${row.exposure_hours}h exposure`}</p>
                        <p className="mt-1 text-xs font-medium text-accent">{row.evidence}</p>
                      </div>
                    ))}
                    {!components.some((row) => row.component_type === type) ? <p className="text-sm text-secondary">No evidence yet.</p> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">Feed response</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">Feed is recorded as a timed event for a spot or group of rods. The early view below counts catches within two hours after feeding, without pretending that timing proves causation.</p>
          {feedSummary.length ? <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">{feedSummary.map((row) => <div key={row.label} className="rounded-lg border border-border bg-raised p-3"><p className="truncate text-sm font-semibold text-primary">{row.label}</p><p className="mt-1 text-xs text-secondary">{row.events} feed event{row.events === 1 ? "" : "s"} · {row.catches} catch{row.catches === 1 ? "" : "es"} within 2h</p></div>)}</div> : <p className="mt-3 text-sm text-secondary">No feed-event evidence yet.</p>}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">Position evidence</h2>
          <p className="mt-1 text-sm leading-6 text-secondary">Position is tracked separately from tackle, so moving a rod does not reset its rig, lead-system or hookbait exposure.</p>
          {positions.length ? <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">{positions.slice(0, 10).map((row) => <div key={row.position_key} className="rounded-lg border border-border bg-raised p-3"><p className="truncate text-sm font-semibold text-primary">{row.label}</p><p className="mt-1 text-xs text-secondary">{row.catches} catch{Number(row.catches) === 1 ? "" : "es"} · {row.interactions} indication{Number(row.interactions) === 1 ? "" : "s"} · {row.periods} fishing period{Number(row.periods) === 1 ? "" : "s"}</p></div>)}</div> : <p className="mt-3 text-sm text-secondary">Position evidence will appear after rods are placed or moved using the live board.</p>}
        </section>

        <section>
          <div className="flex items-start gap-2"><History className="mt-1 size-4 shrink-0 text-accent" /><div><h2 className="text-lg font-semibold text-primary">Session history</h2><p className="mt-1 text-sm text-secondary">Open a year, then a month such as March 2026, to find the sessions from that period.</p></div></div>
          {years.length === 0 ? <div className="mt-3 rounded-lg border border-dashed border-border p-5 text-sm text-secondary">Your sessions will appear here after you start fishing.</div> : (
            <div className="mt-4 space-y-3">
              {years.map((year) => (
                <details key={year.year} className="rounded-xl border border-border bg-raised" open={year === years[0]}>
                  <summary className="cursor-pointer px-4 py-3 font-semibold text-primary">{year.year} <span className="ml-2 text-xs font-normal text-secondary">{year.months.reduce((sum, month) => sum + month.sessions.length, 0)} sessions</span></summary>
                  <div className="space-y-2 border-t border-border p-3">
                    {year.months.map((month) => (
                      <details key={month.key} className="rounded-lg border border-border bg-background">
                        <summary className="cursor-pointer px-3 py-2.5 text-sm font-semibold text-primary">{month.label} <span className="ml-2 text-xs font-normal text-secondary">{month.sessions.length}</span></summary>
                        <div className="space-y-2 border-t border-border p-2">{month.sessions.map((session) => <SessionLink key={session.id} session={session} />)}</div>
                      </details>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2"><Fish className="size-4 text-accent" /><h2 className="text-lg font-semibold text-primary">Recent catches</h2></div>
          <p className="mt-1 text-sm text-secondary">Your latest catches live with your evidence and history rather than cluttering Home.</p>
          {recentCatches.length ? <div className="mt-3 space-y-2">{recentCatches.map((row) => <Link key={row.id} to={`/catches/${row.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-raised p-3"><div className="min-w-0"><p className="truncate text-sm font-semibold text-primary">{weightLabel(row.weight_lb ?? row.weight_lbs, row.weight_oz)} · {row.catch_species === "catfish" ? "Catfish" : row.carp_type && row.carp_type !== "Unknown" ? `${row.carp_type} carp` : "Carp"}</p><p className="mt-0.5 truncate text-xs text-secondary">{row.lake_name || "Fishing session"} · {formatDateTime(row.caught_at)}</p></div><ChevronRight className="size-4 shrink-0 text-secondary" /></Link>)}</div> : <p className="mt-3 text-sm text-secondary">No catches recorded yet.</p>}
        </section>
      </div>
    </>
  );
}

function SessionLink({ session }: { session: any }) {
  return <Link to={session.end_at ? `/sessions/${session.id}` : `/live/${session.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-raised p-3 transition-colors hover:bg-inset"><div className="min-w-0"><p className="truncate text-sm font-semibold text-primary">{session.lake_name || "Fishing session"}{session.peg ? ` · ${session.peg}` : ""}</p><p className="mt-0.5 truncate text-xs text-secondary">{formatDateTime(session.start_at)} · {session.end_at ? "Finished" : "Live"}{session.auto_ended ? session.auto_end_reviewed_at ? " · auto-close reviewed" : " · needs auto-close review" : ""}</p></div><ChevronRight className="size-4 shrink-0 text-secondary" /></Link>;
}
